import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * ForwardDeployed — a 4-step "how we roll out" section, with a large
 * pixelated grocery-scout illustration on the right that fades in.
 */

const steps = [
  {
    n: '1',
    title: 'Book a demo',
    body:
      'We show you how your stores can run demand, ordering, labor, prep and more from one data layer. Built around your operation.',
    cta: true,
  },
  {
    n: '2',
    title: 'We train it on your stores',
    body:
      'We connect your POS and sales history. Each store trains on its own model, fit to its shelves, weather, and local events.',
  },
  {
    n: '3',
    title: 'Start in a few stores',
    body:
      'We go live in three to five locations first, on the piece of the operation where your margin leaks hardest.',
  },
  {
    n: '4',
    title: 'Roll it out to every store',
    body:
      'When it is working, we take it to the rest of the fleet. Each store trains on its own model, same playbook everywhere.',
  },
];

// Pixel grid describing the scout shape (1 = product/pixel, 0 = empty)
// 24 wide × 32 tall — reads as a friendly rounded robot silhouette
const pixelArt = [
  '000000000111111100000000',
  '000000011111111111000000',
  '000000111111111111100000',
  '000001111111111111110000',
  '000011111111111111111000',
  '000011111100011111111000',
  '000111111100011111111100',
  '000111100000000011111100',
  '000111000000000000111100',
  '000111000000000000111100',
  '000111100000000001111100',
  '000011111000000011111000',
  '000001111111111111110000',
  '000000111111111111100000',
  '000000011111111111000000',
  '000000001111111110000000',
  '000000001100000110000000',
  '000000001111111110000000',
  '000000011111111111000000',
  '000000111111111111100000',
  '000001111111111111110000',
  '000011111111111111111000',
  '000111111111111111111100',
  '001111111111111111111110',
  '011111100000000000011111',
  '011111000000000000001111',
  '011110000000000000000111',
  '001100000000000000000011',
  '000000000000000000000000',
  '000000011000000000110000',
  '000000111000000001110000',
  '000000110000000000110000',
];

const PixelBot = ({ reveal }) => {
  const cell = 12;
  const cols = pixelArt[0].length;
  const rows = pixelArt.length;

  // Identify pixel regions that should animate on top of the base reveal:
  //  - eyes (rows 7-10, cols ~10-13) will "blink"
  //  - thruster pixels (rows 29-31) will flicker
  //  - antenna tip (rows 0-1) pulses
  const isEyeLeft = (r, c) => r >= 7 && r <= 10 && c >= 8 && c <= 10;
  const isEyeRight = (r, c) => r >= 7 && r <= 10 && c >= 14 && c <= 16;
  const isThruster = (r) => r >= 29;
  const isAntenna = (r, c) => r <= 1 && c >= 10 && c <= 13;

  return (
    <div
      style={{
        animation: reveal ? 'scoutBob 3.6s ease-in-out infinite' : 'none',
        transformOrigin: 'center bottom',
      }}
    >
      <svg viewBox={`0 0 ${cols * cell} ${rows * cell}`} className="w-full h-auto block">
        <defs>
          <linearGradient id="scoutPixel" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f6f6f2" />
            <stop offset="100%" stopColor="#c9cbd1" />
          </linearGradient>
        </defs>
        {pixelArt.flatMap((row, r) =>
          row.split('').map((v, c) => {
            if (v !== '1') return null;
            const delay = ((r + c) / (rows + cols)) * 900;
            const on = reveal;
            let animation;
            let fill = on ? 'url(#scoutPixel)' : 'rgba(233,233,236,0)';
            if (on && isAntenna(r, c)) {
              animation = 'scoutAntenna 1.4s ease-in-out infinite';
              fill = '#2E6BFF';
            } else if (on && (isEyeLeft(r, c) || isEyeRight(r, c))) {
              animation = 'scoutBlink 4.6s steps(1) infinite';
              fill = '#0b0b0e';
            } else if (on && isThruster(r)) {
              animation = `scoutThruster ${0.6 + ((r + c) % 3) * 0.2}s ease-in-out infinite`;
              fill = '#7ba0ff';
            }
            return (
              <rect
                key={`${r}-${c}`}
                x={c * cell + 1}
                y={r * cell + 1}
                width={cell - 2}
                height={cell - 2}
                fill={fill}
                style={{
                  transition: `fill 500ms ease ${delay}ms`,
                  animation: animation || undefined,
                  animationDelay: animation ? `${((r + c) % 5) * 120}ms` : undefined,
                  transformOrigin: 'center',
                }}
              />
            );
          })
        )}
      </svg>
    </div>
  );
};

export default function ForwardDeployed() {
  const [reveal, setReveal] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setReveal(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} className="section text-white border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6">
        <p className="eyebrow mb-4">The process</p>
        <h2 className="h-display text-[42px] md:text-[56px] leading-[1.02] max-w-[560px] mb-14">
          Forward deployed.
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Steps on left */}
          <div className="lg:col-span-7">
            <div className="relative pl-10">
              {/* Vertical line */}
              <div className="absolute left-3 top-0 bottom-0 w-px bg-white/12" />
              <div
                className="absolute left-3 top-0 w-px bg-[#2E6BFF]"
                style={{
                  height: `${((activeStep + 1) / steps.length) * 100}%`,
                  transition: 'height 500ms ease',
                }}
              />
              {steps.map((s, i) => (
                <div
                  key={s.n}
                  className="relative pb-12 last:pb-0 cursor-pointer"
                  onMouseEnter={() => setActiveStep(i)}
                >
                  {/* Number chip */}
                  <div
                    className="absolute -left-10 top-0 w-6 h-6 flex items-center justify-center border rounded-sm mono text-[11px] transition-colors"
                    style={{
                      background: activeStep >= i ? '#2E6BFF' : 'rgba(5,5,6,0.9)',
                      borderColor: activeStep >= i ? '#2E6BFF' : 'rgba(255,255,255,0.2)',
                      color: activeStep >= i ? '#ffffff' : 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {s.n}
                  </div>
                  <h3 className="h-sans text-white text-[22px] mb-2">{s.title}</h3>
                  <p className="text-white/60 text-[14.5px] leading-[1.65] max-w-[520px] mb-4">
                    {s.body}
                  </p>
                  {s.cta && (
                    <a
                      href="https://calendar.app.google/rUCGByUhUwE69b2T9"
                      target="_blank"
                      rel="noreferrer"
                      className="btn-primary"
                    >
                      Book a Demo <ArrowRight size={16} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Pixelated illustration */}
          <div className="lg:col-span-5 flex items-center justify-center">
            <div
              className="w-full max-w-[300px]"
              style={{
                opacity: reveal ? 1 : 0,
                transform: reveal ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 900ms ease, transform 900ms ease',
              }}
            >
              <PixelBot reveal={reveal} />
              <div className="mono text-[10.5px] text-white/45 text-center mt-6 tracking-widest">
                grocera scout · v1
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
